import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Switch, FormControlLabel, SwitchProps } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnSwitchProps extends Omit<SwitchProps, "onChange"> {
  labelCheck: string;
  labelUncheck: string;
  compact?: boolean;
  isReadOnly?: boolean;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
}

// 부모가 사용할 함수의 타입을 정의합니다.
export interface SnSwitchHandle {
  getChecked: () => boolean;
  setChecked: (value: boolean) => void;
  setReadOnly: (value: boolean) => void;
}
const fontSize = StyleSettings.font;

const CustomSwitch = forwardRef<SnSwitchHandle, SnSwitchProps>(
  (
    {
      labelCheck,
      labelUncheck,
      defaultChecked,
      compact = false,
      isReadOnly: initialReadOnly = false,
      ...props
    },
    ref
  ) => {
    const [localChecked, setLocalChecked] = useState(
      (props.checked as boolean | undefined) ?? defaultChecked ?? false
    );

    const [readOnly, setReadOnly] = useState(initialReadOnly);

    // 외부 checked 값이 바뀌면 내부 표시 상태를 동기화한다.
    useEffect(() => {
      if (typeof props.checked === "boolean") {
        setLocalChecked(props.checked);
      }
    }, [props.checked]);

    // 부모에서 현재 체크 상태를 읽을 수 있도록 노출
    useImperativeHandle(
      ref,
      () => ({
        getChecked: () => localChecked,
        setChecked: (value: boolean) => {
          setLocalChecked(value);
        },
        setReadOnly: (value: boolean) => {
          setReadOnly(value);
        },
      }),
      [localChecked]
    );

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) {
        e.preventDefault();
        return;
      }

      const nextVal = e.target.checked;
      setLocalChecked(nextVal);
      if (props.onChange) {
        props.onChange(e, nextVal);
      }
    };

    const hasLabel =
      (labelCheck || "").trim().length > 0 ||
      (labelUncheck || "").trim().length > 0;

    const switchNode = (
      <Switch
        {...props}
        checked={localChecked}
        onChange={handleToggle}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: "rgb(0,0,255)",
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "rgb(0,0,255)",
          },
        }}
      />
    );

    if (!hasLabel) {
      return (
        <div style={{ margin: compact ? 0 : undefined }}>{switchNode}</div>
      );
    }

    return (
      <FormControlLabel
        labelPlacement="start"
        sx={{
          // compact 모드에서만 좌우 기본 여백 제거
          m: compact ? 0 : undefined,
          ml: compact ? 0 : undefined,
          mr: compact ? 0 : undefined,
          "& .MuiFormControlLabel-label": { fontSize: fontSize },
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
        }}
        control={switchNode}
        label={localChecked ? labelCheck : labelUncheck}
      />
    );
  }
);

export default CustomSwitch;
